import * as Cesium from "cesium";

// Check #6 only (optimized): Connectivity check (start/end open)
// Returns [{ entity, id, reasons }] with reasons:
// - "Start side open"
// - "End side open"
export function getLaneletsWithOpenSides(viewer, currentDataSource) {
  if (!viewer || !viewer.dataSources) return [];
  if (!currentDataSource || !currentDataSource.entities) return [];

  const CONNECT_RADIUS_METERS = 0.005;
  const ellipsoid = (viewer.scene && viewer.scene.globe && viewer.scene.globe.ellipsoid) || Cesium.Ellipsoid.WGS84;
  const RADIUS = ellipsoid.maximumRadius;

  const time = (viewer.clock && viewer.clock.currentTime) || Cesium.JulianDate.now();

  function getPropertyValueFromProps(props, key) {
    if (!props) return undefined;

    if (typeof props.get === "function") {
      const p = props.get(key);
      if (!p) return undefined;
      return typeof p.getValue === "function" ? p.getValue() : p;
    }

    if (props._properties && typeof props._properties === "object") {
      const p = props._properties[key];
      if (!p) return undefined;
      return typeof p.getValue === "function" ? p.getValue() : p;
    }

    if (typeof props.getValue === "function") {
      const obj = props.getValue();
      if (obj && typeof obj === "object") return obj[key];
    }

    if (Object.prototype.hasOwnProperty.call(props, key)) return props[key];

    return undefined;
  }

  function getEntityId(entity) {
    if (!entity) return undefined;
    const props = entity.properties;
    let idFromProps;

    if (props) {
      if (typeof props.get === "function") {
        const p = props.get("id");
        if (p) idFromProps = typeof p.getValue === "function" ? p.getValue() : p;
      }

      if (idFromProps === undefined && props._properties && typeof props._properties === "object") {
        const p = props._properties.id;
        if (p) idFromProps = typeof p.getValue === "function" ? p.getValue() : p;
      }

      if (idFromProps === undefined && typeof props.getValue === "function") {
        const obj = props.getValue();
        if (obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "id")) {
          idFromProps = obj.id;
        }
      }

      if (idFromProps === undefined && Object.prototype.hasOwnProperty.call(props, "id")) {
        idFromProps = props.id;
      }
    }

    if (idFromProps !== undefined && idFromProps !== null) return idFromProps;
    return entity.id;
  }

  function cartesianToFlatCartographic(cart) {
    if (!cart) return null;
    const c = ellipsoid.cartesianToCartographic(cart);
    if (!c) return null;
    c.height = 0;
    return c;
  }

  function horizontalDistanceFromCarto(c1, c2) {
    if (!c1 || !c2) return Number.POSITIVE_INFINITY;
    const dLat = c2.latitude - c1.latitude;
    const dLon = c2.longitude - c1.longitude;
    const cosLat = Math.cos((c1.latitude + c2.latitude) * 0.5);
    const x = RADIUS * dLon * cosLat;
    const y = RADIUS * dLat;
    return Math.sqrt(x * x + y * y);
  }

  function getPolylinePositions(entity, time) {
    if (!entity || !entity.polyline) return null;

    const poly = entity.polyline;
    const positionsProp = poly.positions || poly._positions;
    if (!positionsProp) return null;

    if (typeof positionsProp.getValue === "function") return positionsProp.getValue(time);
    if (Array.isArray(positionsProp)) return positionsProp;
    if (Array.isArray(positionsProp._value)) return positionsProp._value;

    return null;
  }

  // Cache endpoints cartographic per entity (huge win)
  const endpointsCache = new WeakMap();
  function getEndCartos(entity) {
    if (!entity || !entity.polyline) return null;

    const cached = endpointsCache.get(entity);
    if (cached !== undefined) return cached;

    const pos = getPolylinePositions(entity, time);
    if (!Array.isArray(pos) || pos.length === 0) {
      endpointsCache.set(entity, null);
      return null;
    }

    const first = cartesianToFlatCartographic(pos[0]);
    const last = cartesianToFlatCartographic(pos[pos.length - 1]);
    if (!first || !last) {
      endpointsCache.set(entity, null);
      return null;
    }

    const out = { first, last };
    endpointsCache.set(entity, out);
    return out;
  }

  // Build ID -> entity map once (avoid O(N*M) scans)
  function buildIdToEntityMap(viewer) {
    const map = new Map();
    const collection = viewer.dataSources;

    for (let i = 0; i < collection.length; i++) {
      const ds = collection.get(i);
      if (!ds || !ds.entities) continue;

      const ents = ds.entities.values || ds.entities;
      if (!Array.isArray(ents)) continue;

      for (let j = 0; j < ents.length; j++) {
        const e = ents[j];
        if (!e || !e.properties) continue;

        const idVal = getPropertyValueFromProps(e.properties, "id");
        if (idVal === null || idVal === undefined || idVal === "") continue;

        const key = String(idVal);
        if (!map.has(key)) map.set(key, e);
      }
    }

    return map;
  }

  // Build spatial index of ONLY endpoints (much smaller than indexing all vertices)
  function buildEndpointIndex(viewer) {
    const grid = new Map();
    const cellAngle = CONNECT_RADIUS_METERS / RADIUS;

    const collection = viewer.dataSources;

    for (let i = 0; i < collection.length; i++) {
      const ds = collection.get(i);
      if (!ds || !ds.entities) continue;

      const ents = ds.entities.values || ds.entities;
      if (!Array.isArray(ents)) continue;

      for (let j = 0; j < ents.length; j++) {
        const e = ents[j];
        if (!e || !e.polyline) continue;

        const ends = getEndCartos(e);
        if (!ends) continue;

        // Index first + last endpoints only
        const a = ends.first;
        const b = ends.last;

        const gx1 = Math.round(a.longitude / cellAngle);
        const gy1 = Math.round(a.latitude / cellAngle);
        const key1 = gx1 + ":" + gy1;
        let cell1 = grid.get(key1);
        if (!cell1) {
          cell1 = [];
          grid.set(key1, cell1);
        }
        cell1.push({ entity: e, carto: a });

        const gx2 = Math.round(b.longitude / cellAngle);
        const gy2 = Math.round(b.latitude / cellAngle);
        const key2 = gx2 + ":" + gy2;
        let cell2 = grid.get(key2);
        if (!cell2) {
          cell2 = [];
          grid.set(key2, cell2);
        }
        cell2.push({ entity: e, carto: b });
      }
    }

    return { grid, cellAngle };
  }

  function isPointConnectedToOthers(targetCarto, vertexIndex, ownerEntitiesSet) {
    if (!targetCarto) return false;

    const { grid, cellAngle } = vertexIndex;
    if (cellAngle <= 0) return false;

    const gx = Math.round(targetCarto.longitude / cellAngle);
    const gy = Math.round(targetCarto.latitude / cellAngle);

    for (let ix = gx - 1; ix <= gx + 1; ix++) {
      for (let iy = gy - 1; iy <= gy + 1; iy++) {
        const key = ix + ":" + iy;
        const cell = grid.get(key);
        if (!cell) continue;

        for (let k = 0; k < cell.length; k++) {
          const v = cell[k];
          if (!v || !v.carto) continue;
          if (ownerEntitiesSet.has(v.entity)) continue;

          const d = horizontalDistanceFromCarto(targetCarto, v.carto);
          if (d <= CONNECT_RADIUS_METERS) return true;
        }
      }
    }

    return false;
  }

  function pickJoinPoint(laneletCarto, sideFirstCarto, sideLastCarto) {
    const d1 = horizontalDistanceFromCarto(laneletCarto, sideFirstCarto);
    const d2 = horizontalDistanceFromCarto(laneletCarto, sideLastCarto);
    return d1 <= d2 ? sideFirstCarto : sideLastCarto;
  }

  function checkEndpoint(laneletCarto, leftEnds, rightEnds, vertexIndex, ownerEntitiesSet) {
    if (!laneletCarto || !leftEnds || !rightEnds) return null;

    const leftJoin = pickJoinPoint(laneletCarto, leftEnds.first, leftEnds.last);
    const rightJoin = pickJoinPoint(laneletCarto, rightEnds.first, rightEnds.last);

    const centerConnected = isPointConnectedToOthers(laneletCarto, vertexIndex, ownerEntitiesSet);
    const leftConnected = isPointConnectedToOthers(leftJoin, vertexIndex, ownerEntitiesSet);
    const rightConnected = isPointConnectedToOthers(rightJoin, vertexIndex, ownerEntitiesSet);

    return centerConnected && leftConnected && rightConnected;
  }

  const idToEntity = buildIdToEntityMap(viewer);
  const endpointIndex = buildEndpointIndex(viewer);

  const lanelets = currentDataSource.entities.values || currentDataSource.entities;
  if (!Array.isArray(lanelets) || lanelets.length === 0) return [];

  const result = [];
  const seenIds = new Set();

  for (let i = 0; i < lanelets.length; i++) {
    const lanelet = lanelets[i];
    if (!lanelet || !lanelet.properties || !lanelet.polyline) continue;

    const props = lanelet.properties;

    const leftId = getPropertyValueFromProps(props, "left_line_id");
    const rightId = getPropertyValueFromProps(props, "right_line_id");

    if (leftId === null || leftId === undefined || leftId === "") continue;
    if (rightId === null || rightId === undefined || rightId === "") continue;

    const leftEntity = idToEntity.get(String(leftId));
    const rightEntity = idToEntity.get(String(rightId));
    if (!leftEntity || !rightEntity) continue;

    const laneletEnds = getEndCartos(lanelet);
    const leftEnds = getEndCartos(leftEntity);
    const rightEnds = getEndCartos(rightEntity);

    if (!laneletEnds || !leftEnds || !rightEnds) continue;

    const ownerEntitiesSet = new Set([lanelet, leftEntity, rightEntity]);

    const startOk = checkEndpoint(laneletEnds.first, leftEnds, rightEnds, endpointIndex, ownerEntitiesSet);
    const endOk = checkEndpoint(laneletEnds.last, leftEnds, rightEnds, endpointIndex, ownerEntitiesSet);

    // Same behavior as original: invalid only if BOTH ends are open
    if (!startOk && !endOk) {
      const reasons = [];
      if (!startOk) reasons.push("Start side open");
      if (!endOk) reasons.push("End side open");

      const id = getEntityId(lanelet);
      const dedupeKey = id ?? lanelet;
      if (!seenIds.has(dedupeKey)) {
        seenIds.add(dedupeKey);
        result.push({ entity: lanelet, id, reasons });
      }
    }
  }

  return result;
}
